import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react-lite';
import { useInterval } from 'usehooks-ts';
import { useQuery } from '@apollo/client';
import Button from '@components/Button';
import {
  ButtonsContainer,
  Description,
  Error,
  Title,
} from '@components/Interactions/styles';
import { getSinglePoll, PollResult, Polls as TPolls } from '@queries/getPoll';
import PollOptionsRenderer from '@components/Interactions/PollOptionsRenderer';
import API, { apiRoutes, apolloClient } from '@app/api';
import Card from '@components/Interactions/Card';
import { randomTime, REFRESH_INTERVALS } from '@app/utils';
import { useStores } from '@stores';
import { useKeywords } from '@app/hooks';
import PollResultsRenderer from '@components/Interactions/PollResultsRenderer';
import { useHistory } from 'react-router-dom';
import routes from '@routes';
import { useEventSlug } from '@app/hooks/useEventSlug';
import {
  getPollAnswersForUser,
  PollAnswerCheck,
} from '@queries/getPollAnswersForUser';
import { socket } from '../../../socket';

const randomInterval = randomTime(7000, 10000);

type Props = {
  pollSlug: string;
  context?: boolean;
};

const SinglePoll = ({ pollSlug, context = true }: Props) => {
  const [choosenOption, setChoosenOption] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<PollResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pollData, setPollData] = useState<any>(null);

  const history = useHistory();
  const eventSlug = useEventSlug();

  const {
    authStore: { user },
  } = useStores();

  const {
    data: { polls: [poll] = [] } = {},
    loading,
    startPolling,
  } = useQuery<TPolls>(getSinglePoll, {
    variables: { slug: pollSlug },
  });

  const finalPollData = useMemo(() => {
    if (pollData) {
      return {
        ...poll,
        isActive: pollData.isActive,
      };
    }
    return poll;
  }, [poll, pollData]);

  const handlePollSocket = (socketData: any) => {
    if (poll?.documentId === socketData?.documentId) {
      setPollData(socketData);

      if (socketData?.isActive) {
        setShowResults(false);
      } else {
        getResultsData();
      }
    }
  };

  useEffect(() => {
    socket.on('pollUpdated', handlePollSocket);

    return () => {
      socket.off('pollUpdated', handlePollSocket);
    };
  }, [poll]);
  // console.log('test', finalPollData);
  const keywords = useKeywords(finalPollData?.keywords || []);

  const getResults = async () => {
    const results = await API.get<PollResult[]>(
      apiRoutes.POLL_RESULTS(finalPollData?.documentId as string)
    );
    setResults(results.data);
  };

  useInterval(getResults, showResults ? randomInterval : null);

  const handlePollSubmission = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      const { email } = user || {};
      await API.post(apiRoutes.POLL_VOTE, {
        data: { email, pollOptionId: choosenOption },
      });

      await getResults();
      setShowResults(true);
    } catch (err) {
      console.error(err);
      setError("We couldn't submit your vote. Please try again or skip.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getResultsData = async () => {
    await getResults();
    setShowResults(true);
  };

  const checkIfAnswered = async () => {
    const { email } = user || {};

    const res = await apolloClient.query<PollAnswerCheck>({
      query: getPollAnswersForUser,
      variables: { email, pollId: finalPollData?.documentId },
      fetchPolicy: 'no-cache',
    });

    if (res.data.pollAnswers.length > 0) {
      getResultsData();
    }
  };

  useEffect(() => {
    if (context) {
      startPolling(REFRESH_INTERVALS.INTERACTIONS);
    }
  }, []);

  useEffect(() => {
    if (!loading && !finalPollData?.isSinglePoll && !finalPollData) {
      // someone accessed this by url and there is no such a poll
      history.push(routes.EVENT.SOMETHING_WRONG(eventSlug));
    }

    if (!loading && finalPollData?.slug) {
      checkIfAnswered();
    }

    if (!loading && finalPollData?.isSinglePoll && !finalPollData?.isActive) {
      getResultsData();
    }
  }, [finalPollData, loading]);

  return (
    <>
      <Helmet>
        <meta name="Keywords" content={keywords} />
      </Helmet>
      <Card
        lessPadding={context}
        border={context}
        showPlaceholder={loading || !finalPollData}
        enableMinHeight={context}
      >
        <div>
          <Title>{finalPollData?.title}</Title>
          <Description>{finalPollData?.question}</Description>
        </div>

        {showResults && <PollResultsRenderer pollResults={results} />}
        {!showResults && (
          <PollOptionsRenderer
            pollOptions={finalPollData?.pollOptions || []}
            onAnswerClick={setChoosenOption}
            activeOption={choosenOption}
          />
        )}

        <ButtonsContainer>
          {!showResults && (
            <>
              <Button
                disabled={isSubmitting || !choosenOption}
                title="Submit"
                fontSize={14}
                onClick={handlePollSubmission}
              />
            </>
          )}

          {error && <Error>{error}</Error>}
        </ButtonsContainer>
      </Card>
    </>
  );
};

export default observer(SinglePoll);
